import { useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import {
  useFinancialSummary,
  useRemoveReceipt,
  useUploadReceipt,
} from "../../hooks";
import type { Payment, PaymentStatus } from "../../types/financial";

// Class type options
type ClassType = "individual" | "duo" | "group";

// Pricing info based on class type
const pricingInfo: Record<
  ClassType,
  { label: string; price: number; hours: string; description: string }
> = {
  individual: {
    label: "Individual Class",
    price: 360,
    hours: "4 hours/month",
    description: "Private lessons with exclusive attention",
  },
  duo: {
    label: "Duo Class",
    price: 300,
    hours: "4 hours/month",
    description: "Learn with a partner",
  },
  group: {
    label: "Group Class",
    price: 250,
    hours: "4 hours/month",
    description: "Classes with up to 4 students",
  },
};


const whatsappNumber = "558197160139";

export function FinancialPage() {
  // Fetch financial data from API
  const { data: financialSummary, isLoading, error } = useFinancialSummary();
  const uploadReceiptMutation = useUploadReceipt();
  const removeReceiptMutation = useRemoveReceipt();

  // Determine class type from API or default to individual
  const classType = (financialSummary?.classType || "individual") as ClassType;

  // Check if we have real API data
  const hasRealData = Boolean(
    financialSummary?.currentModule?.payments?.length,
  );

  const payments = useMemo(() => {
    return financialSummary?.currentModule?.payments ?? [];
  }, [financialSummary]);

  const currentPricing = useMemo(() => {
    if (financialSummary?.classTypeLabel && financialSummary?.monthlyPrice) {
      return {
        label: financialSummary.classTypeLabel,
        price: financialSummary.monthlyPrice,
        hours: financialSummary.monthlyHours || "4 hours/month",
        description: "",
      };
    }
    return pricingInfo[classType];
  }, [financialSummary, classType]);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<{
    url: string;
    month: number;
    isPdf: boolean;
  } | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<{
    paymentId: string;
    month: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const paidCount = payments.filter((p) => p.status === "paid").length;
  const totalPaid = payments
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + p.amount, 0);
  const totalModule = payments.reduce((sum, p) => sum + p.amount, 0);
  const progressPercentage = Math.round((paidCount / payments.length) * 100);

  const nextPayment = payments.find(
    (p) => p.status === "pending" || p.status === "overdue",
  );

  const getStatusConfig = (status: PaymentStatus) => {
    switch (status) {
      case "paid":
        return {
          label: "Paid",
          icon: "ri-check-line",
          bgColor: "bg-olive-100",
          textColor: "text-olive-700",
          iconColor: "text-olive-600",
        };
      case "pending":
        return {
          label: "Pending",
          icon: "ri-time-line",
          bgColor: "bg-amber-100",
          textColor: "text-amber-700",
          iconColor: "text-amber-600",
        };
      case "overdue":
        return {
          label: "Overdue",
          icon: "ri-error-warning-line",
          bgColor: "bg-red-100",
          textColor: "text-red-700",
          iconColor: "text-red-600",
        };
      case "upcoming":
        return {
          label: "Upcoming",
          icon: "ri-calendar-line",
          bgColor: "bg-earth-100",
          textColor: "text-earth-600",
          iconColor: "text-earth-500",
        };
    }
  };

  const formatDate = (dateStr: string) => {
    // Parse date string as local time to avoid timezone offset issues
    // When parsing "YYYY-MM-DD", JS treats it as UTC midnight, which can shift to previous day in Brazil timezone
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString("en-US", { day: "2-digit", month: "short" });
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const handleFileUpload = (paymentId: string) => {
    // Store the payment ID for when file is selected, but don't show loading yet
    setUploadingId(paymentId);
    fileInputRef.current?.click();
  };

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // If no file selected (user cancelled), reset the uploadingId
    if (!file) {
      setUploadingId(null);
      event.target.value = "";
      return;
    }

    if (!uploadingId) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Unsupported format. Use JPG, PNG, WebP or PDF.");
      setUploadingId(null);
      event.target.value = "";
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum 5MB.");
      setUploadingId(null);
      event.target.value = "";
      return;
    }

    // Upload via API - keep uploadingId until mutation completes
    uploadReceiptMutation.mutate(
      { paymentId: uploadingId, file },
      {
        onSettled: () => {
          setUploadingId(null);
          event.target.value = "";
        },
      },
    );
  };

  const handleRemoveReceipt = (paymentId: string, month: number) => {
    setShowRemoveConfirm({ paymentId, month });
  };

  const confirmRemoveReceipt = () => {
    if (!showRemoveConfirm) return;

    removeReceiptMutation.mutate(showRemoveConfirm.paymentId, {
      onSettled: () => {
        setShowRemoveConfirm(null);
      },
    });
  };

  const whatsappMessage = encodeURIComponent(
    "Hello! I have a question about my payments.",
  );

  const viewReceipt = (payment: Payment) => {
    if (payment.receiptUrl) {
      const isPdf =
        payment.receiptUrl.endsWith(".pdf") || (payment as any).receiptIsPdf;
      setSelectedReceipt({
        url: payment.receiptUrl,
        month: payment.month,
        isPdf: isPdf,
      });
      setShowReceiptModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4 mb-5 sm:mb-8">
          <div className="min-w-0">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 active:text-earth-800 text-xs sm:text-sm mb-1.5 sm:mb-2 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back
            </Link>
            <h1 className="text-xl sm:text-3xl font-display font-bold text-earth-800">
              Financial
            </h1>
            <p className="text-xs sm:text-base text-earth-700 mt-0.5 sm:mt-1">
              {financialSummary?.currentModule?.name || "Current Module"} •{" "}
              {payments.length} installments
            </p>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2 shrink-0">
            <a
              href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white border border-earth-200 hover:border-earth-300 active:border-earth-400 rounded-full text-xs sm:text-sm text-earth-600 transition-colors"
            >
              <i className="ri-whatsapp-line text-green-500"></i>
              <span className="hidden sm:inline">Questions</span>
            </a>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-olive-600"></div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700 text-sm">
              <i className="ri-error-warning-line mr-2"></i>
              Unable to load financial data. Please try again later.
            </p>
          </div>
        )}

        {/* Empty State - No Module */}
        {!isLoading && !error && !hasRealData && (
          <div className="flex justify-center py-12 sm:py-20">
            <div className="bg-white rounded-2xl border border-earth-200 p-8 sm:p-12 max-w-md w-full text-center">
              <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-earth-100 flex items-center justify-center">
                <i className="ri-wallet-3-line text-earth-400 text-3xl"></i>
              </div>
              <h3 className="font-display font-semibold text-earth-800 text-lg mb-2">
                No payment module found
              </h3>
              <p className="text-earth-700 text-sm mb-6">
                Your payment module hasn't been set up yet. Contact us and we'll
                get everything configured for you.
              </p>
              <a
                href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
                  "Hello! I need help setting up my payment module.",
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 active:bg-green-700 text-white rounded-xl font-medium text-sm transition-colors"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Contact Us on WhatsApp
              </a>
            </div>
          </div>
        )}

        {/* Main Grid */}
        {!isLoading && hasRealData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left Column - Progress & Payments (spans 2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              {/* Progress Card */}
              <div className="bg-white rounded-2xl border border-earth-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h2 className="font-display font-semibold text-earth-800 text-sm sm:text-base">
                    Module Progress
                  </h2>
                  <span className="text-xs sm:text-sm text-earth-700">
                    {paidCount} of {payments.length}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="relative h-2.5 sm:h-3 bg-earth-100 rounded-full overflow-hidden mb-3 sm:mb-4">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-olive-500 to-olive-600 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div className="text-center p-2 sm:p-3 min-w-0 bg-olive-50 rounded-xl">
                    <p className="text-sm sm:text-2xl font-bold tabular-nums leading-tight text-olive-700">
                      {formatCurrency(totalPaid)}
                    </p>
                    <p className="text-[10px] sm:text-xs text-earth-700 mt-0.5 sm:mt-1">
                      Paid
                    </p>
                  </div>
                  <div className="text-center p-2 sm:p-3 min-w-0 bg-amber-50 rounded-xl">
                    <p className="text-sm sm:text-2xl font-bold tabular-nums leading-tight text-amber-700">
                      {formatCurrency(totalModule - totalPaid)}
                    </p>
                    <p className="text-[10px] sm:text-xs text-earth-700 mt-0.5 sm:mt-1">
                      Remaining
                    </p>
                  </div>
                  <div className="text-center p-2 sm:p-3 min-w-0 bg-earth-50 rounded-xl">
                    <p className="text-sm sm:text-2xl font-bold tabular-nums leading-tight text-earth-700">
                      {formatCurrency(totalModule)}
                    </p>
                    <p className="text-[10px] sm:text-xs text-earth-700 mt-0.5 sm:mt-1">
                      Total
                    </p>
                  </div>
                </div>
              </div>

              {/* Payments List */}
              <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden">
                <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-earth-100">
                  <h2 className="font-display font-semibold text-earth-800 text-sm sm:text-base">
                    Payment History
                  </h2>
                </div>

                <div className="divide-y divide-earth-100">
                  {payments.map((payment) => {
                    const statusConfig = getStatusConfig(payment.status);
                    return (
                      <div
                        key={payment.id}
                        className={`flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-4 px-3 sm:px-5 py-3 sm:py-4 ${
                          payment.status === "overdue" ? "bg-red-50/50" : ""
                        }`}
                      >
                        {/* Month Number */}
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-earth-100 flex items-center justify-center shrink-0">
                          <span className="text-xs sm:text-sm font-bold text-earth-600">
                            {String(payment.month).padStart(2, "0")}
                          </span>
                        </div>

                        {/* Payment Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-earth-800 text-sm sm:text-base">
                            Installment {payment.month}/{payments[payments.length - 1]?.month || payments.length}
                          </p>
                          <p className="text-xs sm:text-sm text-earth-700">
                            Due: {formatDate(payment.dueDate)}
                            {payment.paidDate && (
                              <span className="text-olive-600 hidden sm:inline">
                                {" "}
                                • Paid {formatDate(payment.paidDate)}
                              </span>
                            )}
                          </p>
                        </div>

                        {/* Amount */}
                        <div className="text-right shrink-0 order-last sm:order-none w-full sm:w-auto mt-2 sm:mt-0 flex items-center justify-between sm:block">
                          <p className="font-semibold text-earth-800 text-sm sm:text-base">
                            {formatCurrency(payment.amount)}
                          </p>
                          {/* Mobile paid date */}
                          {payment.paidDate && (
                            <span className="text-olive-600 text-xs sm:hidden">
                              Paid {formatDate(payment.paidDate)}
                            </span>
                          )}
                        </div>

                        {/* Status Badge */}
                        <div
                          className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full ${statusConfig.bgColor}`}
                        >
                          <i
                            className={`${statusConfig.icon} ${statusConfig.iconColor} text-xs sm:text-sm`}
                          ></i>
                          <span
                            className={`text-[10px] sm:text-xs font-medium ${statusConfig.textColor}`}
                          >
                            {statusConfig.label}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 shrink-0">
                          {/* Upload loading state - only show when mutation is actually pending */}
                          {uploadReceiptMutation.isPending &&
                          uploadReceiptMutation.variables?.paymentId ===
                            payment.id ? (
                            <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-olive-50 rounded-lg">
                              <div className="animate-spin h-4 w-4 border-2 border-olive-600 border-t-transparent rounded-full"></div>
                              <span className="text-xs text-olive-700 font-medium hidden sm:inline">
                                Uploading...
                              </span>
                            </div>
                          ) : payment.receiptUrl ? (
                            <>
                              <button
                                onClick={() => viewReceipt(payment)}
                                className="p-1.5 sm:p-2 text-olive-600 hover:text-olive-700 hover:bg-olive-50 active:bg-olive-100 rounded-lg transition-colors"
                                title="View receipt"
                              >
                                <i className="ri-eye-line text-base sm:text-lg"></i>
                              </button>
                              <button
                                onClick={() =>
                                  handleRemoveReceipt(payment.id, payment.month)
                                }
                                disabled={removeReceiptMutation.isPending}
                                className="p-1.5 sm:p-2 text-red-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                                title="Remove receipt"
                              >
                                <i className="ri-delete-bin-line text-base sm:text-lg"></i>
                              </button>
                            </>
                          ) : hasRealData ? (
                            <button
                              onClick={() => handleFileUpload(payment.id)}
                              className="p-1.5 sm:p-2 text-earth-400 hover:text-olive-600 hover:bg-olive-50 active:bg-olive-100 rounded-lg transition-colors"
                              title="Attach receipt"
                            >
                              <i className="ri-upload-2-line text-base sm:text-lg"></i>
                            </button>
                          ) : (
                            <button
                              disabled
                              className="p-1.5 sm:p-2 text-earth-300 cursor-not-allowed rounded-lg"
                              title="No payment data available"
                            >
                              <i className="ri-upload-2-line text-base sm:text-lg"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Right Column - PIX & Next Payment */}
            <div className="space-y-3 sm:space-y-4">
              {/* Next Payment Alert */}
              {nextPayment && (
                <div
                  className={`p-3 sm:p-4 rounded-2xl border ${
                    nextPayment.status === "overdue"
                      ? "bg-red-50 border-red-200"
                      : "bg-amber-50 border-amber-200"
                  }`}
                >
                  <div className="flex items-start gap-2.5 sm:gap-3">
                    <div
                      className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${
                        nextPayment.status === "overdue"
                          ? "bg-red-100"
                          : "bg-amber-100"
                      }`}
                    >
                      <i
                        className={`ri-notification-3-line text-base sm:text-lg ${
                          nextPayment.status === "overdue"
                            ? "text-red-600"
                            : "text-amber-600"
                        }`}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`font-semibold text-sm sm:text-base ${
                          nextPayment.status === "overdue"
                            ? "text-red-800"
                            : "text-amber-800"
                        }`}
                      >
                        {nextPayment.status === "overdue"
                          ? "Overdue"
                          : "Next due"}
                      </p>
                      <p
                        className={`text-xs sm:text-sm ${
                          nextPayment.status === "overdue"
                            ? "text-red-600"
                            : "text-amber-600"
                        }`}
                      >
                        {formatDate(nextPayment.dueDate)} •{" "}
                        {formatCurrency(nextPayment.amount)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      // Open email client
                      window.location.href = "mailto:";
                    }}
                    className={`w-full mt-2.5 sm:mt-3 py-2 sm:py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-colors ${
                      nextPayment.status === "overdue"
                        ? "bg-red-600 hover:bg-red-700 active:bg-red-800 text-white"
                        : "bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white"
                    }`}
                  >
                    <i className="ri-mail-line mr-1.5 sm:mr-2"></i>
                    Check email
                  </button>
                </div>
              )}

              {/* Your Plan Info */}
              <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden">
                <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-earth-100 flex items-center gap-2">
                  <i className="ri-user-star-line text-olive-600 text-sm sm:text-base"></i>
                  <span className="font-semibold text-earth-800 text-xs sm:text-sm">
                    Your Plan
                  </span>
                </div>

                <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                  {/* Plan Type */}
                  <div className="p-3 sm:p-4 bg-olive-50 rounded-xl">
                    <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                      <span className="text-xs sm:text-sm font-medium text-earth-700">
                        {currentPricing.label}
                      </span>
                      <span className="text-base sm:text-lg font-bold text-olive-700">
                        {formatCurrency(currentPricing.price)}
                      </span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-earth-700">
                      {currentPricing.hours}
                    </p>
                    {currentPricing.description && (
                      <p className="text-[10px] sm:text-xs text-olive-600 mt-0.5 sm:mt-1">
                        {currentPricing.description}
                      </p>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div>
                    <p className="text-[10px] sm:text-xs text-earth-700 uppercase tracking-wide mb-1.5 sm:mb-2">
                      Payment Method
                    </p>
                    <div className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 bg-earth-50 rounded-lg">
                      <i className="ri-bank-card-line text-earth-500 text-base sm:text-lg"></i>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-earth-700">
                          Bank Slip / Card
                        </p>
                        <p className="text-[10px] sm:text-xs text-earth-700">
                          Sent by email
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Reminder Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-3 sm:p-4">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-500 flex items-center justify-center shrink-0">
                    <i className="ri-mail-check-line text-white text-base sm:text-lg"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-earth-800 text-xs sm:text-sm mb-0.5 sm:mb-1">
                      Check your email!
                    </p>
                    <p className="text-[10px] sm:text-xs text-earth-600">
                      Invoice sent before the due date. After payment, attach
                      receipt here.
                    </p>
                  </div>
                </div>
              </div>

              {/* Help Card */}
              <a
                href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200 hover:border-green-300 active:border-green-400 transition-all group"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
                  <i className="ri-whatsapp-line text-white text-lg sm:text-xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-earth-800 text-xs sm:text-sm">
                    Questions or need help?
                  </p>
                  <p className="text-[10px] sm:text-xs text-earth-700">
                    Contact us via WhatsApp for quick assistance.
                  </p>
                </div>
                <i className="ri-arrow-right-line text-green-500 group-hover:translate-x-1 transition-transform"></i>
              </a>

              {/* Payment Info */}
              <div className="bg-earth-50 rounded-2xl p-3 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <i className="ri-information-line text-earth-400 mt-0.5 shrink-0"></i>
                  <div className="text-xs sm:text-sm text-earth-600">
                    <p className="font-medium text-earth-700 mb-0.5 sm:mb-1 text-xs sm:text-sm">
                      Important
                    </p>
                    <ul className="space-y-0.5 sm:space-y-1 text-[10px] sm:text-xs">
                      <li>• Due on the 7th of each month</li>
                      <li>• Invoice sent by email</li>
                      <li>• Credit card available</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileSelected}
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
      />

      {/* Receipt Viewer Modal */}
      {showReceiptModal && selectedReceipt && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-earth-900/50 backdrop-blur-sm"
            onClick={() => {
              setShowReceiptModal(false);
              setSelectedReceipt(null);
            }}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-earth-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-olive-100 flex items-center justify-center">
                  <i className="ri-file-text-line text-olive-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h3 className="font-display font-semibold text-earth-800 text-sm sm:text-base">
                    Receipt - Inst. {selectedReceipt.month}/{payments[payments.length - 1]?.month || payments.length}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-earth-700">
                    {selectedReceipt.isPdf ? "PDF" : "Image"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowReceiptModal(false);
                  setSelectedReceipt(null);
                }}
                className="p-2 hover:bg-earth-100 active:bg-earth-200 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-lg sm:text-xl text-earth-400"></i>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-3 sm:p-5 bg-earth-50">
              <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
                {selectedReceipt.isPdf ? (
                  <iframe
                    src={selectedReceipt.url}
                    className="w-full h-[400px] sm:h-[500px] rounded-xl border border-earth-200 bg-white"
                    title="PDF Receipt"
                  />
                ) : selectedReceipt.url.includes("drive.google.com") ? (
                  // For Google Drive images, show link to open in new tab
                  <div className="text-center p-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-olive-100 flex items-center justify-center">
                      <i className="ri-image-line text-olive-600 text-2xl"></i>
                    </div>
                    <p className="text-earth-600 mb-4 text-sm">
                      Receipt stored in Google Drive
                    </p>
                    <a
                      href={selectedReceipt.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-xl text-sm font-medium transition-colors"
                    >
                      <i className="ri-external-link-line"></i>
                      View Receipt
                    </a>
                  </div>
                ) : (
                  <img
                    src={selectedReceipt.url}
                    alt={`Receipt Installment ${selectedReceipt.month}`}
                    className="max-w-full max-h-[400px] sm:max-h-[500px] rounded-xl border border-earth-200 shadow-sm object-contain bg-white"
                    onError={(e) => {
                      // If image fails to load, show fallback
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      target.parentElement?.insertAdjacentHTML(
                        "beforeend",
                        `<div class="text-center p-8">
                          <div class="w-16 h-16 mx-auto mb-4 rounded-xl bg-amber-100 flex items-center justify-center">
                            <i class="ri-error-warning-line text-amber-600 text-2xl"></i>
                          </div>
                          <p class="text-earth-600 mb-4 text-sm">Unable to preview image</p>
                          <a href="${selectedReceipt.url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-xl text-sm font-medium transition-colors">
                            <i class="ri-external-link-line"></i>
                            Open in new tab
                          </a>
                        </div>`,
                      );
                    }}
                  />
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-t border-earth-100 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0 shrink-0">
              <p className="text-[10px] sm:text-xs text-earth-700">
                Receipt attached
              </p>
              <div className="flex gap-2 w-full sm:w-auto">
                <a
                  href={selectedReceipt.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-earth-100 hover:bg-earth-200 active:bg-earth-300 text-earth-700 rounded-xl text-xs sm:text-sm font-medium transition-colors"
                >
                  <i className="ri-external-link-line"></i>
                  Open
                </a>
                <button
                  onClick={() => {
                    setShowReceiptModal(false);
                    setSelectedReceipt(null);
                  }}
                  className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 bg-olive-600 hover:bg-olive-700 active:bg-olive-800 text-white rounded-xl text-xs sm:text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Receipt Confirmation Modal */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-earth-900/50 backdrop-blur-sm"
            onClick={() => setShowRemoveConfirm(null)}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                <i className="ri-delete-bin-line text-red-600 text-lg sm:text-xl"></i>
              </div>
              <div>
                <h3 className="font-display font-semibold text-earth-800 text-sm sm:text-base">
                  Remove Receipt?
                </h3>
                <p className="text-xs sm:text-sm text-earth-700">
                  Installment {showRemoveConfirm.month}/{payments[payments.length - 1]?.month || payments.length}
                </p>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-earth-600 mb-5">
              This will remove the receipt and set the payment status back to
              pending. You can upload a new receipt later.
            </p>

            <div className="flex gap-2">
              <button
                onClick={() => setShowRemoveConfirm(null)}
                disabled={removeReceiptMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-earth-100 hover:bg-earth-200 active:bg-earth-300 text-earth-700 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveReceipt}
                disabled={removeReceiptMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {removeReceiptMutation.isPending ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Removing...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    Remove
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
